import {
  ButtonGroup,
  Grid,
  GridItem,
  PrimaryButton,
  TextInputField,
} from "@certn/cascadia-react";
import { Field, Form, Formik } from "formik";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { object, string } from "yup";

import { SettingsEditItem } from "../../../components/Settings/SettingsEditItem";
import { useFormSubmit } from "../../../hooks/api/useSubmit";
import { useUser } from "../../../hooks/api/useUser";
import { getPrimaryName } from "../../../utils/user-utils";

export function NameSettings() {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const toggle = () => setExpanded((curr) => !curr);
  const { user, mutate } = useUser();
  const userName = user && getPrimaryName(user);

  const handleSubmit = useFormSubmit("/internal/people/me/name/update/", {
    onSuccess: () => {
      toggle();
      mutate();
    },
  });

  // 1+ letters with spaces, apostrophes and hyphens allowed anywhere except first and last character
  const validNameRegex = /^((\p{L}+)|(\p{L}[\p{L}\s'-]*\p{L}))$/u;
  const validationText = t("Please only use letters");

  return (
    <SettingsEditItem
      setting={t("Legal Name")}
      value={`${userName?.first_name} ${userName?.middle_name} ${userName?.last_name}`}
      description={t(
        "We recommend using the information on a government issued identity document or birth certificate as this may be used to auto-populate forms."
      )}
      expanded={expanded}
      toggle={toggle}
      testID={expanded ? "legal-name-cancel-link" : "legal-name-edit-link"}
    >
      <Formik
        initialValues={{
          first_name: userName?.first_name || "",
          middle_name: userName?.middle_name || "",
          last_name: userName?.last_name || "",
        }}
        onSubmit={handleSubmit}
        validationSchema={object({
          first_name: string().matches(validNameRegex, validationText),
          middle_name: string().matches(validNameRegex, validationText),
          last_name: string().matches(validNameRegex, validationText),
        })}
      >
        <Form>
          <Grid width="416px">
            <GridItem>
              <Field
                name="first_name"
                label={t("First name")}
                as={TextInputField}
                isRequired
              />
            </GridItem>
            <GridItem>
              <Field
                name="middle_name"
                label={t("Middle name")}
                as={TextInputField}
              />
            </GridItem>
            <GridItem>
              <Field
                name="last_name"
                label={t("Last name")}
                as={TextInputField}
                isRequired
              />
            </GridItem>
            <GridItem>
              <ButtonGroup>
                <PrimaryButton
                  type="submit"
                  size="medium"
                  testID="legal-name-save-button"
                >
                  {t("Save")}
                </PrimaryButton>
              </ButtonGroup>
            </GridItem>
          </Grid>
        </Form>
      </Formik>
    </SettingsEditItem>
  );
}
